import type { Locale } from "../../../lib/i18n/locales";

/**
 * The dashboard banner's look per NOC (user, 2026-10-31: "every dashboard
 * must feel welcoming based on what NOC is logged in"). Keyed by the
 * organisation's IOC code (`/auth/me.group.organization.code`); `SIN` is the
 * stored spelling of Singapore, as in the flags. Anything else — the admins,
 * the host's own staff, a NOC with no code — gets the HOST theme: Malaysia.
 *
 * `greeting` is the welcome in the NOC's own language, shown by the mascot
 * whatever language the GMS is in: like a name, it is the NOC's, not a UI
 * string, so it is data here and never a translation key. Several = the
 * bubble cycles through them (Singapore's four official languages). `font`
 * names the GMS locale whose webfont draws the script (`ensureLocaleFont`).
 *
 * Colours are the flag's: `ribbon` the bands that flow along the bottom (in
 * flag order), `ink` the landmark's silhouette (a deep tone that reads on the
 * banner's white), `sun` the disc behind it, `gold` the landmark's details.
 * All drawing is in the app (`landmarks.tsx`): no image is fetched, so the
 * banner draws offline like the flags.
 */
export type LandmarkKey = "BRU" | "CAM" | "INA" | "LAO" | "MAS" | "MYA" | "PHI" | "SGP" | "THA" | "TLS" | "VIE";

export interface Greeting {
	text: string;
	/** BCP 47, for the `lang` attribute (screen readers, line breaking). */
	lang: string;
}

export interface NocTheme {
	landmark: LandmarkKey;
	greeting: readonly Greeting[];
	/** The GMS locale whose webfont the greeting needs (Thai, Lao, Khmer, Myanmar scripts). */
	font?: Locale;
	ribbon: readonly [string, string, string];
	ink: string;
	sun: string;
	gold: string;
}

const MS: Greeting = { text: "Selamat datang!", lang: "ms" };

export const HOST_THEME: NocTheme = {
	landmark: "MAS",
	greeting: [MS],
	ribbon: ["#010066", "#cc0001", "#ffcc00"],
	ink: "#16186b",
	sun: "#ffcc00",
	gold: "#cc0001",
};

const THEMES: Record<LandmarkKey, NocTheme> = {
	BRU: {
		landmark: "BRU",
		greeting: [MS],
		ribbon: ["#f7e017", "#1a1a1a", "#cf1126"],
		ink: "#26304a",
		sun: "#f7e017",
		gold: "#e0b000",
	},
	CAM: {
		landmark: "CAM",
		greeting: [{ text: "សូមស្វាគមន៍!", lang: "km" }],
		font: "km",
		ribbon: ["#032ea1", "#e00025", "#032ea1"],
		ink: "#0b2a86",
		sun: "#f4b400",
		gold: "#e00025",
	},
	INA: {
		landmark: "INA",
		greeting: [{ text: "Selamat datang!", lang: "id" }],
		ribbon: ["#ce1126", "#f3f3f3", "#ce1126"],
		ink: "#6e1420",
		sun: "#ff8a3d",
		gold: "#ce1126",
	},
	LAO: {
		landmark: "LAO",
		greeting: [{ text: "ຍິນດີຕ້ອນຮັບ!", lang: "lo" }],
		font: "lo",
		ribbon: ["#ce1126", "#002868", "#ce1126"],
		ink: "#002868",
		sun: "#ffd76a",
		gold: "#d9a400",
	},
	MAS: { ...HOST_THEME },
	MYA: {
		landmark: "MYA",
		greeting: [{ text: "ကြိုဆိုပါတယ်!", lang: "my" }],
		font: "my",
		ribbon: ["#fecb00", "#34b233", "#ea2839"],
		ink: "#1d5e25",
		sun: "#fecb00",
		gold: "#d99a00",
	},
	PHI: {
		landmark: "PHI",
		greeting: [{ text: "Mabuhay!", lang: "fil" }],
		ribbon: ["#0038a8", "#ce1126", "#fcd116"],
		ink: "#0b3a91",
		sun: "#fcd116",
		gold: "#ce1126",
	},
	SGP: {
		landmark: "SGP",
		greeting: [
			{ text: "Welcome!", lang: "en" },
			{ text: "欢迎!", lang: "zh" },
			{ text: "Selamat datang!", lang: "ms" },
			{ text: "வணக்கம்!", lang: "ta" },
		],
		ribbon: ["#ef3340", "#f3f3f3", "#ef3340"],
		ink: "#7f1d27",
		sun: "#ffd6d9",
		gold: "#ef3340",
	},
	THA: {
		landmark: "THA",
		greeting: [{ text: "ยินดีต้อนรับ!", lang: "th" }],
		font: "th",
		ribbon: ["#a51931", "#2d2a4a", "#a51931"],
		ink: "#2d2a4a",
		sun: "#f6b73c",
		gold: "#d4a017",
	},
	TLS: {
		landmark: "TLS",
		greeting: [
			{ text: "Benvindu!", lang: "tet" },
			{ text: "Bem-vindo!", lang: "pt" },
		],
		ribbon: ["#dc241f", "#ffc726", "#1a1a1a"],
		ink: "#6b1712",
		sun: "#ffc726",
		gold: "#dc241f",
	},
	VIE: {
		landmark: "VIE",
		greeting: [{ text: "Chào mừng!", lang: "vi" }],
		ribbon: ["#da251d", "#ffcd00", "#da251d"],
		ink: "#1e5b57",
		sun: "#ffcd00",
		gold: "#da251d",
	},
};

const ALIASES: Record<string, LandmarkKey> = { SIN: "SGP" };

/** The theme for an organisation's IOC code; null when there is none for it (the caller falls back to the host). */
export function nocThemeFor(code: string | null | undefined): NocTheme | null {
	if (!code) return null;
	const key = ALIASES[code] ?? code;
	return key in THEMES ? THEMES[key as LandmarkKey] : null;
}
